import { TableOfContentsItem } from "@/models/table-of-contents";
import { ParagraphItem } from "@/partials/document-renderer/hooks/useParagraphExtraction";

// ---------------------------------------------------------------------------
// Tree structure
// ---------------------------------------------------------------------------

export interface TocTreeNode {
  item: TableOfContentsItem;
  children: TocTreeNode[];
}

/**
 * Converts a flat depth-based TOC array into a nested tree.
 *
 * The algorithm uses a stack of { node, depth } entries. For each item we pop
 * the stack until we find an entry whose depth is strictly less than the
 * current item's depth -- that entry is the parent. If no such entry exists
 * the item becomes a root node.
 */
export const buildTocTree = (items: TableOfContentsItem[]): TocTreeNode[] => {
  const roots: TocTreeNode[] = [];
  const stack: { node: TocTreeNode; depth: number }[] = [];

  for (const item of items) {
    const node: TocTreeNode = { item, children: [] };

    // Pop until we find a parent with strictly smaller depth
    while (stack.length > 0 && stack[stack.length - 1].depth >= item.depth) {
      stack.pop();
    }

    if (stack.length === 0) {
      roots.push(node);
    } else {
      stack[stack.length - 1].node.children.push(node);
    }

    stack.push({ node, depth: item.depth });
  }

  return roots;
};

// ---------------------------------------------------------------------------
// Jump-to navigation
// ---------------------------------------------------------------------------

/**
 * Maps table-of-contents entries onto the document header's jump dropdown.
 *
 * Vejledninger, cirkulærer and skrivelser carry no `§` structure, so paragraph
 * extraction finds nothing to offer. Their numbered section outline lives in the
 * server-built table of contents instead (LAR-126), and it targets ids in the
 * same `htmlWithIds` the viewer renders.
 */
export const tocItemsToJumpOptions = (
  items: TableOfContentsItem[] | null | undefined
): ParagraphItem[] => {
  if (!items?.length) return [];

  const seen = new Set<string>();
  const options: ParagraphItem[] = [];

  for (const item of items) {
    const id = item.destinationHtmlId?.trim();
    if (!id || seen.has(id)) continue;

    const rawLabel = item.sourceText?.trim() || item.destinationText?.trim();
    const label = rawLabel?.replace(/\u00a0/g, " ").trim();
    if (!label) continue;

    seen.add(id);
    options.push({ id, label });
  }

  return options;
};

export type DocumentJumpNavigationKind = "paragraph" | "section";

export type DocumentJumpNavigation = {
  items: ParagraphItem[];
  kind: DocumentJumpNavigationKind;
};

type ResolveDocumentJumpNavigationArgs = {
  /** `§` items extracted from the rendered HTML; wins whenever it has entries. */
  paragraphItems: ParagraphItem[];
  /** The server-built outline, used only when the document has no `§` structure. */
  tocItems?: TableOfContentsItem[] | null;
  /**
   * A consolidation rebuilds the DOM under its own container ids, so the table of
   * contents — which addresses `htmlWithIds` — cannot resolve against it.
   */
  isConsolidatedRender: boolean;
};

/**
 * Picks what the document header's jump dropdown offers, and which copy it needs.
 *
 * Falls back to the section outline only for documents that have no `§` at all,
 * so laws and bekendtgørelser keep their existing paragraph navigation untouched.
 */
export const resolveDocumentJumpNavigation = ({
  paragraphItems,
  tocItems,
  isConsolidatedRender,
}: ResolveDocumentJumpNavigationArgs): DocumentJumpNavigation => {
  if (paragraphItems.length > 0 || isConsolidatedRender) {
    return { items: paragraphItems, kind: "paragraph" };
  }

  const sectionItems = tocItemsToJumpOptions(tocItems);
  return sectionItems.length > 0
    ? { items: sectionItems, kind: "section" }
    : { items: paragraphItems, kind: "paragraph" };
};
